import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/**
 * Listado buscable para elegir el cliente de trabajo activo (localStorage).
 * @param {boolean} [requireSearch=false] — si true, la lista permanece contraída
 *   hasta que el usuario teclee (útil en filtros con muchas empresas).
 */
export function ClientWorkPickerList({
  clients,
  clientId,
  onSelect,
  className = "",
  autoFocusSearch = false,
  requireSearch = false,
}) {
  const [searchQ, setSearchQ] = useState("");

  useEffect(() => {
    setSearchQ("");
  }, [clientId]);

  const hasSearch = Boolean(searchQ.trim());
  const emptyCatalog = clients.length === 0;
  // Catálogo vacío: mostrar el aviso real, no «escriba para buscar».
  const listCollapsed = requireSearch && !hasSearch && !emptyCatalog;

  const filteredClients = useMemo(() => {
    const q = searchQ.trim().toUpperCase();
    if (!q) return requireSearch && !emptyCatalog ? [] : clients;
    return clients.filter((c) => {
      const blob = [c.razon_social, c.nombre_comercial, c.rfc, c.email]
        .filter(Boolean)
        .join(" ")
        .toUpperCase();
      return blob.includes(q);
    });
  }, [clients, searchQ, requireSearch, emptyCatalog]);

  const handleSelect = (id) => {
    onSelect(id);
    setSearchQ("");
  };

  return (
    <div className={className} data-testid="client-work-picker-list">
      <div className={`relative ${listCollapsed ? "mb-0" : "mb-3"}`}>
        <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <Input
          type="search"
          value={searchQ}
          onChange={(e) => setSearchQ(e.target.value)}
          placeholder="Buscar por nombre o RFC…"
          className="pl-8 h-9 text-sm bg-white"
          data-testid="client-work-picker-search"
          aria-label="Buscar cliente por nombre o RFC"
          autoFocus={autoFocusSearch}
        />
      </div>
      {listCollapsed ? (
        <p className="text-[11px] text-slate-500 m-0 mt-2" data-testid="client-work-picker-search-hint">
          Hay {clients.length} empresa{clients.length === 1 ? "" : "s"} registrada{clients.length === 1 ? "" : "s"}.
          {" "}Escriba el nombre o RFC para mostrar la lista.
        </p>
      ) : (
        <>
          {clients.length > 0 && hasSearch ? (
            <p className="text-[11px] text-slate-500 mb-2">
              {filteredClients.length} de {clients.length} cliente(s)
            </p>
          ) : null}
          <ul
            className="max-h-72 overflow-y-auto rounded-sm border border-slate-200 bg-white divide-y divide-slate-100"
            data-testid="client-work-picker-results"
          >
            {filteredClients.length === 0 ? (
              <li className="px-4 py-6 text-center text-sm text-slate-500">
                {clients.length === 0
                  ? "No hay clientes registrados. Use Gestionar clientes para dar de alta."
                  : "Ningún cliente coincide con la búsqueda."}
              </li>
            ) : (
              filteredClients.map((c) => {
                const active = c.id === clientId;
                const initials = (c.razon_social || c.rfc || "?")
                  .trim()
                  .split(/\s+/)
                  .filter(Boolean)
                  .slice(0, 2)
                  .map((w) => w[0])
                  .join("")
                  .toUpperCase() || "?";
                return (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={() => handleSelect(c.id)}
                      data-testid={`client-work-picker-row-${c.id}`}
                      className={[
                        "w-full text-left px-4 py-3 flex items-center gap-3 transition-colors",
                        active ? "bg-emerald-50/80 hover:bg-emerald-50" : "hover:bg-slate-50",
                      ].join(" ")}
                    >
                      <span
                        className={[
                          "flex h-8 w-8 shrink-0 items-center justify-center rounded-sm border text-xs font-bold",
                          active
                            ? "border-[#3DA873] bg-[#0F2D5C] text-white"
                            : "border-slate-200 bg-slate-50 text-slate-500",
                        ].join(" ")}
                        aria-hidden
                      >
                        {active ? <CheckCircle2 className="w-4 h-4" /> : initials.slice(0, 2)}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-semibold text-[#0F2D5C] truncate">
                          {c.razon_social || "Sin razón social"}
                        </span>
                        <span className="block text-xs font-mono text-slate-600 truncate">{c.rfc}</span>
                      </span>
                      {active ? (
                        <span className="text-[9px] font-bold uppercase tracking-wide text-[#3DA873] shrink-0">
                          Activo
                        </span>
                      ) : null}
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </>
      )}
    </div>
  );
}

/**
 * Diálogo modal para cambiar el cliente de trabajo sin salir de la pantalla actual.
 * mode="require": aviso al entrar a una tarjeta sin cliente seleccionado.
 */
export default function ClientWorkPickerDialog({
  open,
  onOpenChange,
  clients,
  clientId,
  onSelect,
  mode = "change",
  pendingLabel = "",
}) {
  const requireMode = mode === "require";
  const handleSelect = (id) => {
    onSelect(id);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg rounded-sm max-h-[90vh] overflow-y-auto"
        data-testid={requireMode ? "client-required-picker-dialog" : "client-work-picker-dialog"}
      >
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "'Chivo'" }}>
            {requireMode ? "Favor de seleccionar cliente" : "Cambiar cliente de trabajo"}
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-600">
            {requireMode
              ? (
                <>
                  Para continuar
                  {pendingLabel ? (
                    <>
                      {" "}en <strong>{pendingLabel}</strong>
                    </>
                  ) : null}
                  {" "}elija el cliente de trabajo. Puede cerrar este aviso y seleccionar después
                  en la barra superior.
                </>
              )
              : (
                <>
                  Elige con cuál RFC vas a operar. Queda predeterminado en Visor, Confronta, Carga y el resto
                  de pestañas hasta que lo cambies de nuevo.
                </>
              )}
          </DialogDescription>
        </DialogHeader>
        <ClientWorkPickerList
          clients={clients}
          clientId={clientId}
          onSelect={handleSelect}
          autoFocusSearch
        />
        <div className="flex flex-wrap items-center justify-between gap-2 pt-1 border-t border-slate-100">
          <p className="text-[11px] text-slate-500 m-0">
            {clientId
              ? "Cliente predeterminado guardado para toda la sesión."
              : "Selecciona un cliente de la lista para comenzar."}
          </p>
          <div className="flex items-center gap-3 shrink-0">
            <Link
              to="/clientes"
              className="text-xs text-[#0F2D5C] font-medium underline"
              onClick={() => onOpenChange(false)}
            >
              Gestionar clientes
            </Link>
            <button
              type="button"
              className="text-xs font-semibold text-slate-600 hover:text-[#0F2D5C] underline"
              onClick={() => onOpenChange(false)}
              data-testid="client-required-picker-close"
            >
              Cerrar
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
